import React from 'react';
import { Box, Typography, Button, Container } from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';

// Context
import { useI18n } from '../../context/I18nContext';

const HeroContainer = styled.section`
  height: 80vh;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  text-align: center;
  background-image: linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.4)), url('/assets/images/backgrounds/beach_road.jpg');
  background-size: cover;
  background-position: center;
  color: var(--light-text);
  margin-top: -5px; /* Para eliminar qualquer gap entre o header e o hero */
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, rgba(0, 131, 135, 0.3) 0%, rgba(0, 0, 0, 0.4) 100%);
    opacity: 0.6;
    z-index: 1;
  }
`;
const HeroContent = styled(Box)`
  position: relative;
  z-index: 2;
  max-width: 900px;
  padding: 0 2rem;
  width: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  
  h1 {
    text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
    margin-bottom: 1.5rem;
    font-weight: 700;
    font-size: 3.5rem;
    letter-spacing: 1px;
  }
  
  h2 {
    text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5);
    margin-bottom: 2.5rem;
    font-weight: 400;
    font-size: 1.8rem;
    max-width: 800px;
    line-height: 1.4;
  }
`;

const ButtonsContainer = styled(Box)`
  display: flex;
  gap: 1.5rem;
  justify-content: center;
  margin-top: 2.5rem;
  width: 100%;
  
  @media (max-width: 600px) {
    flex-direction: column;
    align-items: center;
    gap: 1rem;
  }
`;

const HeroSection: React.FC = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  
  return (
    <HeroContainer id="home">
      <Container>
        <HeroContent>
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6 }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
              <Typography variant="h1" component="h1" sx={{ mb: 0, fontSize: { xs: '2.5rem', md: '3.5rem' }, fontWeight: 700, textAlign: 'center', lineHeight: 1.2 }}>
                TURoad
              </Typography>
              <Typography variant="h1" component="div" sx={{ mt: 0.5, fontSize: { xs: '1.8rem', md: '2.5rem' }, fontWeight: 600, textAlign: 'center', lineHeight: 1.3 }}>
                Histórias que ligam pessoas a lugares no Brasil
              </Typography>
            </Box>
            
            <Typography variant="h4" component="h2">
              {t('hero_subtitle')}
            </Typography>
            
            <ButtonsContainer>
              <Button 
                onClick={() => navigate('/about')}
                variant="contained" 
                color="primary" 
                size="large"
                sx={{ 
                  borderRadius: '30px', 
                  px: 4, 
                  py: 1.5, 
                  fontSize: '1.1rem',
                  fontWeight: 500,
                  boxShadow: '0 4px 14px rgba(0, 131, 135, 0.4)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-3px)',
                    boxShadow: '0 6px 20px rgba(0, 131, 135, 0.6)'
                  }
                }}
              >
                {t('cta_learn_more')}
              </Button>
              
              <Button 
                onClick={() => navigate('/cidades')}
                variant="contained" 
                color="secondary" 
                size="large"
                sx={{ 
                  borderRadius: '30px', 
                  px: 4, 
                  py: 1.5, 
                  fontSize: '1.1rem',
                  fontWeight: 500,
                  boxShadow: '0 4px 14px rgba(255, 82, 82, 0.4)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-3px)',
                    boxShadow: '0 6px 20px rgba(255, 82, 82, 0.6)'
                  }
                }}
              >
                {t('cta_explore')}
              </Button>
            </ButtonsContainer>
          </motion.div>
        </HeroContent>
      </Container>
    </HeroContainer>
  );
};

export default HeroSection;
